import React from "react";

/**
 * Co Stock brand mark.
 *
 * One continuous white stroke: a wide "C" whose top terminal breaks out of the
 * curve and flicks upward. The C carries the name, the flick carries the point of
 * the product, and because it is a single unbroken line there is nothing to
 * dissolve into noise when it is scaled down.
 *
 * That constraint is the whole design. The previous mark packed three separate
 * bars into 32px and, at favicon size, they merged into an unreadable smudge —
 * detail that survives on a marketing page does not survive in a browser tab. One
 * shape, thick stroke, generous gap.
 *
 * Drawn with currentColor so it inherits from whatever it sits in, and rendered as
 * pure SVG so there is no image request and no dark-mode variant to maintain.
 */
export function LogoMark({ size = 32, className = "", rounded = true }) {
  return (
    <span
      className={`inline-grid place-items-center shrink-0 ${rounded ? "rounded-xl" : ""} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 32 32" width={size} height={size} fill="none">
        <rect width="32" height="32" rx={rounded ? 9 : 0} fill="currentColor" />
        {/* Tick, then straight into the arc — one path, so the join never breaks. */}
        <path
          d="M25.4 4.9 L20.59 9.45 A8 8 0 1 0 20.59 22.55"
          stroke="white"
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/** Mark plus wordmark. `tone="light"` for use on a dark or coloured background. */
export default function Logo({ size = 32, tone = "dark", className = "", showWord = true }) {
  // On a coloured background the mark's own square would fight the surface, so it
  // drops to a translucent white and lets the stroke do the work.
  const markColour = tone === "light" ? "text-white/20" : "text-brand-500";
  const wordColour = tone === "light" ? "text-white" : "text-slate-900";
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark size={size} className={markColour} />
      {showWord && (
        <span
          className={`font-heading font-bold tracking-tight ${wordColour}`}
          style={{ fontSize: Math.round(size * 0.62) }}
        >
          Co&nbsp;Stock
        </span>
      )}
    </span>
  );
}
